import React, { useEffect, useMemo, useState } from "react";
import { formatoDecimal } from "../../../utilities/formatoNumeros";
import TopSeller from "./TopSeller";
import TopTipoPublicaciones from "./TopTipoPublicaciones";
import { normalizeTarjetasData } from "./DashboardHelper";

const tablaEstilo = {
  fondo: "#fffafb",
  header: "#ffe5e7",
  borde: "1px solid #ffd0d5",
};

const BadgeVar = ({ label, value, suffix = "%" }) => {
  const n = Number(value ?? 0);
  const isNeg = n < 0;
  return (
    <span
      className={`badge mx-1 ${isNeg ? "badge-danger" : "badge-success"}`}
      style={{ fontSize: 12 }}
      title={`${label}: ${n.toFixed(2)}${suffix}`}
    >
      {label}: {n.toFixed(2)}
      {suffix}
    </span>
  );
};

export default function TarjetasDashboard({
  data,
  onSelectProduct,
  tabProductos,
  setTabProductos,
  topSellers,
  tipoPublicaciones,
  userType,
  currentUserType,
}) {
  const d = normalizeTarjetasData(data);
  const isFullAdmin = currentUserType == 1 || currentUserType == 2;
  const isAuthorizedSeller = !!(userType?.isSeller || userType?.isSellerAdmin);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);

  const slides = useMemo(() => {
    const availableSlides = [
      {
        id: "top-productos",
        title: "Top Productos",
        icon: "mdi mdi-trophy-variant-outline",
        subtitle: "Productos con mejor performance de ventas",
      },
    ];

    if (isFullAdmin) {
      availableSlides.push({
        id: "top-sellers",
        title: "Top Sellers",
        icon: "mdi mdi-account-multiple",
        subtitle: "Sellers con mayor volumen de unidades",
      });
    }

    if (isFullAdmin || isAuthorizedSeller) {
      availableSlides.push({
        id: "top-tipos",
        title: "Top Tipo Publicaciones",
        icon: "mdi mdi-format-list-bulleted-type",
        subtitle: "Tipos de publicación con mejor rendimiento",
      });
    }

    return availableSlides;
  }, [isAuthorizedSeller, isFullAdmin]);

  useEffect(() => {
    if (tabProductos >= slides.length) {
      setTabProductos(0);
    }
  }, [slides.length, tabProductos, setTabProductos]);

  useEffect(() => {
    if (slides.length <= 1 || isCarouselPaused) {
      return undefined;
    }

    const timer = setInterval(() => {
      setTabProductos((tabProductos + 1) % slides.length);
    }, 3000);

    return () => clearInterval(timer);
  }, [isCarouselPaused, setTabProductos, slides.length, tabProductos]);

  const goToSlide = (targetIndex) => {
    if (slides.length <= 0) return;
    const safeIndex = (targetIndex + slides.length) % slides.length;
    setTabProductos(safeIndex);
  };

  return (
    <>
      <div className="row position-relative mb-4">
        <div className="col-12 col-lg-3 mb-3">
          <div className="p-3 rounded shadow-sm h-100 dashboard-atributos text-center">
            <h6 className="text-muted mb-1">Total Ventas</h6>
            <h3 className="m-0">${formatoDecimal(`${d.ventasDelMes}`)}</h3>
            <div className="mt-2">
              <BadgeVar label="Var. mensual ventas" value={d.variacionMensualVentas} />
              <BadgeVar label="Var. anual ventas" value={d.variacionAnualVentas} />
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-3 mb-3">
          <div className="p-3 rounded shadow-sm h-100 dashboard-atributos text-center">
            <h6 className="text-muted mb-1">Comisión Ventas</h6>
            <h3 className="m-0">${formatoDecimal(`${d.comisionVentas}`)}</h3>
          </div>
        </div>

        <div className="col-12 col-lg-3 mb-3">
          <div className="p-3 rounded shadow-sm h-100 dashboard-atributos text-center">
            <h6 className="text-muted mb-1">Ticket Promedio</h6>
            <h3 className="m-0">${formatoDecimal(`${d.ticketPromedio}`)}</h3>
          </div>
        </div>

        <div className="col-12 col-lg-3 mb-3">
          <div className="p-3 rounded shadow-sm h-100 dashboard-atributos text-center">
            <h6 className="text-muted mb-1">Unidades Vendidas</h6>
            <h3 className="m-0">{d.unidadesVendidas}</h3>
            <div className="mt-2">
              <BadgeVar label="Var. mensual unidades" value={d.variacionMensualUnidades} />
              <BadgeVar label="Var. anual unidades" value={d.variacionAnualUnidades} />
            </div>
          </div>
        </div>
      </div>

      <div className="col-12">
        <div className="p-3 rounded shadow-sm dashboard-container">
          <div
            className="dashboard-top-carousel"
            onMouseEnter={() => setIsCarouselPaused(true)}
            onMouseLeave={() => setIsCarouselPaused(false)}
          >
            <div className="dashboard-top-carousel__header mb-3">
              <div className="d-flex align-items-center">
                <div className="dashboard-top-carousel__icon mr-2">
                  <i className={slides[tabProductos]?.icon || "mdi mdi-trophy-variant-outline"} />
                </div>
                <div>
                  <h5 className="m-0">{slides[tabProductos]?.title || "Top Productos"}</h5>
                  <small className="text-muted">
                    {slides[tabProductos]?.subtitle || "Resumen de topes del período seleccionado"}
                  </small>
                </div>
              </div>
            </div>

            <div className="dashboard-top-carousel__body">
              {slides.length > 1 && (
                <button
                  type="button"
                  className="dashboard-top-carousel__arrow dashboard-top-carousel__arrow--left"
                  onClick={() => goToSlide(tabProductos - 1)}
                  aria-label="Ir al bloque anterior"
                >
                  <i className="mdi mdi-chevron-left" />
                </button>
              )}

              <div className="dashboard-top-carousel__viewport">
                {tabProductos === 0 && (
                  <section className="dashboard-top-carousel__slide">
                    <div className="table-responsive mb-1">
                      <table className="table table-sm table-hover mb-0">
                        <thead className="thead-light">
                          <tr>
                            <th style={{ minWidth: 120 }}>Código</th>
                            <th>Producto</th>
                            <th className="text-right" style={{ minWidth: 120 }}>
                              Unidades
                            </th>
                            <th className="text-right" style={{ minWidth: 160 }}>
                              Ventas
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {(d.topProductos || []).length > 0 ? (
                            d.topProductos.map((p, idx) => (
                              <tr
                                key={`${p.codigoProducto}-${idx}`}
                                role="button"
                                style={{ cursor: "pointer" }}
                                onClick={() => onSelectProduct && onSelectProduct(p)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    onSelectProduct && onSelectProduct(p);
                                  }
                                }}
                                tabIndex={0}
                                title="Buscar publicaciones por este código"
                              >
                                <td>{p.codigoProducto}</td>
                                <td>{p.nombreProducto}</td>
                                <td className="text-right">{p.unidadesVendidas}</td>
                                <td className="text-right">${formatoDecimal(`${p.montoVentas}`)}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={4} className="text-center text-muted py-3">
                                Sin datos
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {isFullAdmin && tabProductos === 1 && (
                  <section className="dashboard-top-carousel__slide">
                    <TopSeller sellers={topSellers} tablaEstilo={tablaEstilo} />
                  </section>
                )}

                {(isFullAdmin || isAuthorizedSeller) && tabProductos === 2 && (
                  <section className="dashboard-top-carousel__slide">
                    <TopTipoPublicaciones tipos={tipoPublicaciones} tablaEstilo={tablaEstilo} />
                  </section>
                )}
              </div>

              {slides.length > 1 && (
                <button
                  type="button"
                  className="dashboard-top-carousel__arrow dashboard-top-carousel__arrow--right"
                  onClick={() => goToSlide(tabProductos + 1)}
                  aria-label="Ir al bloque siguiente"
                >
                  <i className="mdi mdi-chevron-right" />
                </button>
              )}
            </div>

            {slides.length > 1 && (
              <div className="dashboard-top-carousel__dots mt-3">
                {slides.map((slide, index) => (
                  <button
                    key={slide.id}
                    type="button"
                    className={`dashboard-top-carousel__dot ${tabProductos === index ? "is-active" : ""}`}
                    onClick={() => goToSlide(index)}
                    aria-label={`Ir a ${slide.title}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
